// Einstellungen. Die Kennzeichen-Schalter (docs/reference/einstellungen-
// oben.png, -unten.png, -sekunden-menue.png, 02.09.2026) bilden den
// Original-Bildschirm 1:1 ab, siehe lib/einstellungen.ts fuer die
// Feld-fuer-Feld-Begruendung jeder Vorgabe. Bewusst NICHT gebaut:
// "Freunde-Leaderboard (Beta)" - unser Leaderboard vergleicht laut E15
// immer alle freigeschalteten Nutzer der Instanz, es gibt kein Konzept
// von hinzugefuegten Freunden zum An-/Abschalten; die Wahl "Nur Punkte"/
// "Kennzeichen" aus demselben Screenshot lebt deshalb schon als eigener,
// nicht gespeicherter Zustand auf der Leaderboard-Seite selbst. Ebenso
// nicht gebaut: "Datenschutz" (eigener Rechtstext, noch nicht verfasst)
// und "Datenbank löschen" (Umfang braucht erst eine Entscheidung: nur
// eigene Eintraege, oder das ganze Konto).
//
// Was aus Phase 2 schon da war (Name, Sammlungen, Verwaltung-Link,
// Abmelden), zieht hier ein, damit es nicht verloren geht. Seit Phase 6
// dazu: gemeinsame Sammlungen anlegen, beitreten, verwalten.

import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, fehlertext } from "../lib/api.js";
import { useBundle } from "../lib/bundle.js";
import { aktiveSammlung, useSammlungAnlegen } from "../lib/collections.js";
import {
  useEinstellungenSpeichern,
  type Einstellungen as EinstellungenWerte,
  type LandSichtbarkeit,
} from "../lib/einstellungen.js";
import type { Nutzer, Sammlung } from "../lib/me.js";
import { vollstaendigeReihenfolge } from "../lib/treffer.js";
import { Hinweis, HilfeIcon, Karte, Knopf, Schalter } from "../bausteine/Bausteine.js";
import { LaenderSortieren } from "../bausteine/LaenderSortieren.js";
import { SammlungsZeile } from "../bausteine/SammlungsZeile.js";
import { Unterseite } from "../bausteine/Unterseite.js";
import { PushEinstellungen } from "../bausteine/PushEinstellungen.js";
import { AppSymbolAuswahl } from "../bausteine/AppSymbolAuswahl.js";
import { KurzbefehlEinstellungen } from "../bausteine/KurzbefehlEinstellungen.js";

const SEKUNDEN_OPTIONEN = [0, 1, 2, 3, 4] as const;

function sekundenText(wert: 0 | 1 | 2 | 3 | 4): string {
  return wert === 0 ? "aus" : wert === 1 ? "nach 1 Sekunde" : `nach ${wert} Sekunden`;
}

export function Einstellungen({ nutzer, sammlungen }: { nutzer: Nutzer; sammlungen: Sammlung[] }) {
  const queryClient = useQueryClient();
  const [name, setzeName] = useState(nutzer.displayName ?? "");
  const [nameAendern, setNameAendern] = useState(false);
  const [neueSammlung, setNeueSammlung] = useState("");
  const [sekundenMenueOffen, setSekundenMenueOffen] = useState(false);
  const [laenderVerwaltenOffen, setLaenderVerwaltenOffen] = useState(false);
  const sammlungAnlegen = useSammlungAnlegen();
  const einstellungenSpeichern = useEinstellungenSpeichern();
  const bundle = useBundle();

  const settings = (nutzer.settings ?? {}) as EinstellungenWerte;
  const aktive = aktiveSammlung(sammlungen, settings.activeCollectionId);

  // "Länder verwalten" oeffnet dasselbe Modal wie der Stift im Drawer
  // (bausteine/LaenderSortieren.tsx, IMG_4924) - ein zweiter, eigener
  // Bildschirm dafuer waere doppelt gebaute Funktionalitaet.
  const laender = useMemo(() => bundle.data?.laender ?? [], [bundle.data?.laender]);
  const nutzerReihenfolge = useMemo(() => settings.countryOrder ?? [], [settings.countryOrder]);
  const sichtbarkeit = useMemo(() => settings.countryVisibility ?? {}, [settings.countryVisibility]);
  const vollstaendigeLaenderreihenfolge = useMemo(
    () => vollstaendigeReihenfolge(laender, nutzerReihenfolge),
    [laender, nutzerReihenfolge],
  );

  // Fehlt ein Schalter (kein Eintrag in users.settings), gilt die im
  // Referenz-Screenshot gezeigte Vorgabe - siehe lib/einstellungen.ts.
  const punkteAusblenden = settings.punkteAusblenden ?? false;
  const historischeAusblenden = settings.historischeAusblenden ?? false;
  const diplomatenAnzeigen = settings.diplomatenAnzeigen ?? true;
  const globaleSuche = settings.globaleSuche ?? true;
  const eingabeLoeschenBeiNeu = settings.eingabeLoeschenBeiNeu ?? true;
  const eingabeLoeschenBeiVorhandenSekunden = settings.eingabeLoeschenBeiVorhandenSekunden ?? 0;
  const dunklerModus = settings.dunklerModus ?? false;

  const abmelden = useMutation({
    mutationFn: () => api<void>("/auth/logout", { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });

  // Wie auf der Warteseite (E4): Von Google uebernehmen wir den Namen nie,
  // er ist ausschliesslich hier zu setzen. Anders als dort ist das Konto
  // schon aktiv, deshalb kein Redirect-Vorschlag, nur der bisherige Wert.
  const nameSpeichern = useMutation({
    mutationFn: () => api<{ displayName: string }>("/me", { method: "PATCH", body: { displayName: name } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });

  function nameSpeichernAbsenden(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length < 2) return;
    nameSpeichern.mutate();
    setNameAendern(false);
  }

  async function sammlungAnlegenAbsenden(e: FormEvent) {
    e.preventDefault();
    const eingabe = neueSammlung.trim();
    if (eingabe.length < 2) return;
    try {
      await sammlungAnlegen.mutateAsync(eingabe);
      setNeueSammlung("");
    } catch {
      // Fehler steht in sammlungAnlegen.error und wird darueber angezeigt.
    }
  }

  return (
    <Unterseite
      titel="Einstellungen"
      kinder={
        <>
          <Karte
            kinder={
              <>
                <h2 className="mb-1 text-base font-medium">
                  Hallo {nutzer.displayName ?? nutzer.email}
                </h2>
                <p className="mb-3 text-sm text-neutral-600">
                  Dieser Name steht an deinen Einträgen und im Leaderboard.
                </p>

                {nameSpeichern.isSuccess && nutzer.displayName && (
                  <Hinweis art="gut" kinder={`Gespeichert: ${nutzer.displayName}`} />
                )}
                {nameSpeichern.error && <Hinweis art="fehler" kinder={fehlertext(nameSpeichern.error)} />}

                {/* Der Name stand hier als offenes Eingabefeld, also sah es
                    aus, als sei er staendig in Arbeit. Aendern ist weiter
                    erlaubt, kostet jetzt aber einen Klick. */}
                {nameAendern ? (
                  <form className="flex gap-2" onSubmit={nameSpeichernAbsenden}>
                    <input
                      autoFocus
                      value={name}
                      onChange={(e) => setzeName(e.target.value)}
                      placeholder="Anna"
                      maxLength={40}
                      className="min-h-11 flex-1 rounded border border-neutral-300 px-3 text-sm outline-none focus:border-akzent"
                    />
                    <Knopf
                      type="submit"
                      art="neben"
                      disabled={nameSpeichern.isPending || name.trim().length < 2}
                      kinder="Speichern"
                    />
                    <Knopf
                      art="neben"
                      onClick={() => {
                        setzeName(nutzer.displayName ?? "");
                        setNameAendern(false);
                      }}
                      kinder="Abbrechen"
                    />
                  </form>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="min-h-11 flex-1 content-center text-sm">
                      {nutzer.displayName ?? <span className="text-neutral-400">noch kein Name</span>}
                    </span>
                    <Knopf art="neben" onClick={() => setNameAendern(true)} kinder="Ändern" />
                  </div>
                )}
              </>
            }
          />

          <Karte
            kinder={
              <div className="-mx-4 -my-4 divide-y divide-neutral-100">
                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="flex items-center gap-2 text-sm">
                    Punkte ausblenden <HilfeIcon />
                  </span>
                  <Schalter
                    an={punkteAusblenden}
                    onAendern={(wert) => einstellungenSpeichern.mutate({ punkteAusblenden: wert })}
                    label="Punkte ausblenden"
                  />
                </div>

                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="flex items-center gap-2 text-sm">
                    Historische Kennzeichen ausblenden <HilfeIcon />
                  </span>
                  <Schalter
                    an={historischeAusblenden}
                    onAendern={(wert) => einstellungenSpeichern.mutate({ historischeAusblenden: wert })}
                    label="Historische Kennzeichen ausblenden"
                  />
                </div>

                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="flex items-center gap-2 text-sm">
                    Diplomatenkennzeichen anzeigen <HilfeIcon />
                  </span>
                  <Schalter
                    an={diplomatenAnzeigen}
                    onAendern={(wert) => einstellungenSpeichern.mutate({ diplomatenAnzeigen: wert })}
                    label="Diplomatenkennzeichen anzeigen"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setLaenderVerwaltenOffen(true)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm text-neutral-900"
                >
                  Länder verwalten
                  <span aria-hidden="true" className="text-neutral-400">
                    ›
                  </span>
                </button>

                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="flex items-center gap-2 text-sm">
                    Globale Suche <HilfeIcon />
                  </span>
                  <Schalter
                    an={globaleSuche}
                    onAendern={(wert) => einstellungenSpeichern.mutate({ globaleSuche: wert })}
                    label="Globale Suche"
                  />
                </div>

                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="text-sm">
                    Eingabefeld löschen, wenn das Kennzeichen als gesehen markiert wird
                  </span>
                  <Schalter
                    an={eingabeLoeschenBeiNeu}
                    onAendern={(wert) => einstellungenSpeichern.mutate({ eingabeLoeschenBeiNeu: wert })}
                    label="Eingabefeld löschen, wenn das Kennzeichen als gesehen markiert wird"
                  />
                </div>

                <div className="relative flex items-center justify-between gap-3 px-4 py-3">
                  <span className="text-sm">
                    Eingabefeld löschen, wenn das Kennzeichen bereits gesehen wurde
                  </span>
                  <button
                    type="button"
                    onClick={() => setSekundenMenueOffen((offen) => !offen)}
                    aria-label="Wartezeit wählen"
                    className="flex shrink-0 items-center gap-0.5 px-1 text-neutral-500"
                  >
                    <span className="h-1 w-1 rounded-full bg-current" />
                    <span className="h-1 w-1 rounded-full bg-current" />
                    <span className="h-1 w-1 rounded-full bg-current" />
                  </button>
                  {sekundenMenueOffen && (
                    <div className="absolute right-4 top-full z-10 mt-1 w-44 rounded bg-flaeche-inhalt py-1 text-sm shadow-lg ring-1 ring-black/5">
                      {SEKUNDEN_OPTIONEN.map((wert) => (
                        <button
                          key={wert}
                          type="button"
                          onClick={() => {
                            einstellungenSpeichern.mutate({ eingabeLoeschenBeiVorhandenSekunden: wert });
                            setSekundenMenueOffen(false);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-neutral-50"
                        >
                          <span className="w-4 text-akzent">
                            {wert === eingabeLoeschenBeiVorhandenSekunden ? "✓" : ""}
                          </span>
                          {sekundenText(wert)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="text-sm">Dunkler Modus</span>
                  {/* Rein optisch: dunkler Modus selbst ist bewusst
                      zurueckgestellt (heller Modus bevorzugt), siehe
                      lib/einstellungen.ts. */}
                  <Schalter
                    an={dunklerModus}
                    onAendern={(wert) => einstellungenSpeichern.mutate({ dunklerModus: wert })}
                    label="Dunkler Modus"
                  />
                </div>
              </div>
            }
          />

          <Karte
            kinder={
              <>
                <h2 className="mb-1 text-base font-medium">Deine Sammlungen</h2>
                <p className="mb-2 text-sm text-neutral-500">
                  Der Hauptscreen sammelt gerade in: <strong>{aktive?.name ?? "–"}</strong>
                </p>
                {aktive?.kind === "shared" && (
                  <label className="mb-2 flex items-center gap-2 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      checked={settings.spiegleInEigene ?? false}
                      onChange={(e) => einstellungenSpeichern.mutate({ spiegleInEigene: e.target.checked })}
                      className="h-4 w-4"
                    />
                    Neue Funde zusätzlich in der eigenen Sammlung speichern
                  </label>
                )}
                <ul>
                  {sammlungen.map((s) => (
                    <SammlungsZeile
                      key={s.id}
                      sammlung={s}
                      aktiv={s.id === aktive?.id}
                      onAktivSchalten={() => einstellungenSpeichern.mutate({ activeCollectionId: s.id })}
                    />
                  ))}
                </ul>

                {sammlungAnlegen.error && <Hinweis art="fehler" kinder={fehlertext(sammlungAnlegen.error)} />}

                <form
                  className="mt-3 flex gap-2 border-t border-neutral-100 pt-3"
                  onSubmit={(e) => void sammlungAnlegenAbsenden(e)}
                >
                  <input
                    value={neueSammlung}
                    onChange={(e) => setNeueSammlung(e.target.value)}
                    placeholder="Name der neuen Sammlung"
                    className="min-w-0 flex-1 rounded border border-neutral-300 px-3 py-2 text-sm"
                  />
                  <Knopf
                    type="submit"
                    art="neben"
                    disabled={sammlungAnlegen.isPending || neueSammlung.trim().length < 2}
                    kinder="Anlegen"
                  />
                </form>
              </>
            }
          />

          <AppSymbolAuswahl />

          <PushEinstellungen nutzer={nutzer} />
          <KurzbefehlEinstellungen sammlungen={sammlungen} />

          <div className="flex flex-wrap gap-2">
            <Link
              to="/import"
              className="inline-flex min-h-11 items-center rounded border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Kennzeichen importieren
            </Link>
            <Link
              to="/backup"
              className="inline-flex min-h-11 items-center rounded border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Drive-Backup
            </Link>
            {nutzer.role === "admin" && (
              <Link
                to="/verwaltung"
                className="inline-flex min-h-11 items-center rounded border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                Verwaltung
              </Link>
            )}
            <Knopf art="neben" onClick={() => abmelden.mutate()} kinder="Abmelden" />
          </div>

          <LaenderSortieren
            offen={laenderVerwaltenOffen}
            onClose={() => setLaenderVerwaltenOffen(false)}
            laender={laender}
            reihenfolge={vollstaendigeLaenderreihenfolge}
            sichtbarkeit={sichtbarkeit}
            onReihenfolgeAendern={(neu) => einstellungenSpeichern.mutate({ countryOrder: neu })}
            onSichtbarkeitAendern={(iso2, naechste: LandSichtbarkeit) =>
              einstellungenSpeichern.mutate({ countryVisibility: { ...sichtbarkeit, [iso2]: naechste } })
            }
          />
        </>
      }
    />
  );
}
